import {useEffect, useState, useRef} from "react";
import moment from "moment-timezone";
import {FaPlay, FaPause} from "react-icons/fa";
import {Scroll} from "lucide-react";

import {VoicemailTranscriptModal} from "./VoicemailTranscriptModal";

/**
 * RecordingPlayer Component
 * Handles individual audio playback, seeking, and marking notifications as read.
 */
const RecordingPlayer = ({recordingUrl, contactId, notifications, onNotificationRead}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (recordingUrl && !audioRef.current) {
      const audio = new Audio(recordingUrl);
      audioRef.current = audio;

      const handleTimeUpdate = () => {
        if (audio.duration && isFinite(audio.duration)) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setProgress(100);
      };

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);

      return () => {
        audio.pause();
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("pause", handlePause);
        audioRef.current = null;
      };
    }
  }, [recordingUrl]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      try {
        if (audio.ended || progress >= 100) {
          audio.currentTime = 0;
          setProgress(0);
        }
        await audio.play();

        // Mark as read logic
        if (
          contactId ===
          notifications.find((n) => n.ContactId === contactId)?.ContactId
        ) {
          const options = {
            method: "PATCH",
            body: JSON.stringify({
              ContactId: contactId,
              source_notification: "Voicemail",
            }),
            headers: {
              "Content-Type": "application/json",
              Authorization: process.env.REACT_APP_AUTH_TOKEN,
            },
          };
          fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/notifications`, options)
            .then((resp) => resp.json())
            .then(() => onNotificationRead?.())
            .catch((error) => console.error("Error marking read:", error));
        }
      } catch (error) {
        console.error("Error attempting to play audio:", error.message);
      }
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = (e.target.value / 100) * audio.duration;
    audio.currentTime = newTime;
    setProgress(e.target.value);
  };

  const currentTime = audioRef.current
    ? Math.floor(audioRef.current.currentTime)
    : 0;

  const displayDuration =
    duration && isFinite(duration) ? Math.floor(duration) : 0;

  const format = (s) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  if (!recordingUrl)
    return <span className="text-gray-400 text-xs italic">No Recording</span>;

  return (
    <div className="flex items-center space-x-2 w-full max-w-xs bg-gray-50 p-2 rounded-lg border border-gray-200">
      <button onClick={togglePlay} className="p-1 text-[#2ba9cd]">
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      <div className="flex-1 flex items-center">
        <input
          type="range"
          min="0"
          max="100"
          value={progress}
          onChange={handleSeek}
          className="w-full h-2 bg-gray-300 rounded-full appearance-none cursor-pointer accent-[#2ba9cd]"
          style={{
            background: `linear-gradient(to right, #2ba9cd ${progress}%, #d1d5db ${progress}%)`,
          }}
        />
      </div>
      <span className="text-xs text-gray-600 font-mono whitespace-nowrap">
        {format(currentTime)} / {format(displayDuration)}
      </span>
    </div>
  );
};

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd] rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
  </div>
);

/**
 * LogsView Component
 */
const LogsView = ({logs, notifications, isLoading, onNotificationRead}) => {
  const [modalData, setModalData] = useState({
    open: false,
    transcription: null,
  });
  const headers = [
    "",
    "Time",
    "Phone Number",
    "Customer Name",
    "Program",
    "Recording",
    "Transcript",
  ];

  return (
    <div className="flex flex-col p-4 space-y-6">
      {isLoading ? (
        <div className="flex justify-center items-center h-[75vh]">
          <OrbitLoader />
        </div>
      ) : (
        <div className="h-[75vh] overflow-y-auto overflow-x-auto bg-white rounded-xl shadow-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                {headers.map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.map((log) => (
                <tr
                  key={log.ContactId}
                  className={`hover:bg-gray-50 transition duration-150`}
                >
                  <td className="p-2 text-sm text-gray-600 justify-center flex">
                    {log.highlighted && (
                      <div className="flex items-center">
                        <div
                          className="h-2 w-2 bg-red-600 rounded-full shadow-[0_0_8px_rgba(220,38,38,0.5)]"
                          title="New notification"
                        />
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-sm text-gray-600">
                    {moment
                      .tz(log.start_time, "UTC")
                      .format("Do MMM YYYY hh:mm A")}
                  </td>
                  <td className="p-2 text-sm text-gray-600">
                    {log.customer_phonenumber}
                  </td>
                  <td className="p-2 text-sm text-gray-600">
                    {!log.customer_firstname
                      ? "Unknown"
                      : `${log.customer_firstname} ${log.customer_lastname}`}
                  </td>
                  <td className="p-2 text-sm text-gray-600">
                    {log.client_name}
                  </td>
                  <td className="p-2 text-sm text-gray-600 w-64">
                    <RecordingPlayer
                      recordingUrl={log.recordingUrl}
                      contactId={log.ContactId}
                      notifications={notifications}
                      onNotificationRead={onNotificationRead}
                    />
                  </td>
                  <td className="p-2 text-sm text-gray-600 justify-center flex">
                    <Scroll
                      className="cursor-pointer text-gray-400 hover:text-cyan-500 transition-colors"
                      onClick={() =>
                        setModalData({
                          open: true,
                          transcription:
                            log.transcription || "No transcript available",
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <VoicemailTranscriptModal
            isOpen={modalData.open}
            onClose={() => setModalData({open: false, transcription: null})}
            transcription={modalData.transcription}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Main Voicemails Component
 */
const Voicemails = (props) => {
  const [voicemails, setVoicemails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedInitially = useRef(false);

  useEffect(() => {
    let queues = props.agentQueues
      ?.map((x) => (x && x?.name !== "null" ? x.name.toLowerCase() : null))
      .filter(Boolean);

    if (queues && queues.length > 0) {
      if (!hasLoadedInitially.current) {
        setIsLoading(true);
      }

      const promises = queues.map((queue) =>
        fetch(
          `${process.env.REACT_APP_AWS_ENDPOINT}/voicemails/client_name/${queue}`,
          {
            method: "GET",
            headers: {"Content-Type": "application/json"},
          },
        )
          .then((resp) => resp.json())
          .then((data) => (data.status_code === 200 ? data.body : []))
          .catch((error) => {
            console.error(`Error fetching queue ${queue}:`, error);
            return [];
          }),
      );

      Promise.all(promises).then((results) => {
        const flatLogs = results.flat();
        flatLogs.sort(
          (a, b) => new Date(b.start_time) - new Date(a.start_time),
        );
        const updatedVoicemails = flatLogs.map((voicemail) => ({
          ...voicemail,
          highlighted: props.notifications.some(
            (n) => n.ContactId === voicemail.ContactId,
          ),
        }));
        setVoicemails(updatedVoicemails);
        hasLoadedInitially.current = true;
        setIsLoading(false);
      });
    }
  }, [props.agentQueues, props.notifications]);

  return (
    <LogsView
      logs={voicemails}
      notifications={props.notifications}
      isLoading={isLoading}
      onNotificationRead={props.onNotificationRead}
    />
  );
};

export default Voicemails;
